// Клиентский «чёрный ящик» сетевой диагностики (эпизод «телечат нестабилен без VPN», 2026-07-05).
//
// Задача: увидеть С УСТРОЙСТВА ПОЛЬЗОВАТЕЛЯ, что именно рвётся на плохой сети — WS (какие коды
// закрытия), REST (сетевые сбои/тормоза) или всё сразу, — когда доступа к консоли устройства нет.
//
// Механика: заметные сетевые события пишутся в кольцевой буфер localStorage (переживает перезагрузку
// страницы и офлайн); при появлении связи (ws.open / успешная проба /healthz / возврат сети / таймер)
// буфер досылается пачками на POST /diag/client-log, где api-server кладёт записи в структурный лог
// (src=client-diag). При разрыве WS запускается серия активных проб GET /healthz — это главный
// дискриминатор гипотез: «WS падает, а HTTP ходит» против «падает всё».
//
// Место в keyhole: модуль живёт в client-transport (владелец сетевых примитивов; страж app-ui
// запрещает прямые fetch/WebSocket). app-ui лишь композирует: оборачивает fetchImpl/wsFactory
// транспорта через wrapFetch()/wrapWs() и зовёт start(). Никаких доменных типов не вводится.
//
// Fail-safe: диагностика НИКОГДА не ломает основной поток — все свои операции в try/catch,
// обёртки прозрачны (ошибки base-вызовов пробрасываются как есть), flush-сбои не логируются
// в буфер (иначе петля самовозбуждения).
import { CSRF_HEADER, REQUESTED_WITH_HEADER, REQUESTED_WITH_VALUE, defaultCsrfSource } from './http.js';
const STORAGE_KEY = 'tc-diag-v1';
const FLUSH_PATH = '/diag/client-log';
const HEALTHZ_PATH = '/healthz';
const FLUSH_BATCH = 120;
const STR_CAP = 200;
const D_KEYS_CAP = 12;
function capStr(v, n) {
    return String(v).slice(0, n);
}
/** Приводит произвольный словарь к безопасному плоскому виду (капы размеров, только примитивы). */
function sanitizeData(d) {
    const out = {};
    let i = 0;
    for (const [k, v] of Object.entries(d)) {
        if (i++ >= D_KEYS_CAP)
            break;
        out[capStr(k, 32)] =
            typeof v === 'number' || typeof v === 'boolean' || v === null ? v : capStr(v, STR_CAP);
    }
    return out;
}
/** Имя ошибки без стеков/URL (в буфер не тащим ничего тяжёлого/чувствительного). */
function errName(err) {
    if (err instanceof Error)
        return capStr(`${err.name}: ${err.message}`, 120);
    return capStr(err, 120);
}
/** Путь без origin и query (не светим токены/параметры в диагностике). */
function pathOf(input) {
    const url = typeof input === 'string'
        ? input
        : typeof input?.url === 'string'
            ? input.url
            : String(input);
    try {
        // относительные пути парсим от фиктивного origin
        return new URL(url, 'http://x').pathname;
    }
    catch {
        return capStr(url, 80);
    }
}
function defaultStorage() {
    try {
        const ls = globalThis.localStorage;
        if (ls === undefined)
            return null;
        // Safari private mode: setItem бросает — проверяем сразу, чтобы дальше не ловить на каждом log().
        ls.setItem(`${STORAGE_KEY}-probe`, '1');
        ls.removeItem(`${STORAGE_KEY}-probe`);
        return ls;
    }
    catch {
        return null;
    }
}
function defaultFetch() {
    // bind к globalThis: HttpEngine-урок — голая ссылка на fetch теряет this (transport.ts, R2).
    return (...args) => fetch(...args);
}
function defaultWsCtor() {
    return (url, protocols) => new WebSocket(url, protocols);
}
/** Хост из URL (для диагностики мульти-edge переключений; без query/path). */
function hostOf(url) {
    try {
        return new URL(url).host;
    }
    catch {
        return capStr(url, 40);
    }
}
export function createConnectionDiag(opts) {
    const baseUrl = opts.baseUrl.replace(/\/+$/, '');
    const now = opts.now ?? Date.now;
    const maxEntries = opts.maxEntries ?? 400;
    const probeIntervalMs = opts.probeIntervalMs ?? 20_000;
    const probesPerEpisode = opts.probesPerEpisode ?? 8;
    const flushIntervalMs = opts.flushIntervalMs ?? 60_000;
    const setIntervalFn = opts.setIntervalFn ?? ((fn, ms) => setInterval(fn, ms));
    const clearIntervalFn = opts.clearIntervalFn ?? ((h) => clearInterval(h));
    const setTimeoutFn = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
    const clearTimeoutFn = opts.clearTimeoutFn ?? ((h) => clearTimeout(h));
    const storage = opts.storage === undefined ? defaultStorage() : opts.storage;
    const csrfSource = opts.csrfSource ?? defaultCsrfSource();
    const netFetch = opts.fetchImpl ?? defaultFetch();
    const nav = opts.navigatorRef === undefined
        ? globalThis.navigator ?? null
        : opts.navigatorRef;
    const win = opts.windowRef === undefined
        ? (globalThis.window ?? null)
        : opts.windowRef;
    const doc = opts.documentRef === undefined
        ? (globalThis.document ?? null)
        : opts.documentRef;
    // ── буфер ────────────────────────────────────────────────────────────────────────────────────
    const sid = Math.random().toString(36).slice(2, 8);
    let seq = 0;
    let entries = load();
    function load() {
        if (storage === null)
            return [];
        try {
            const raw = storage.getItem(STORAGE_KEY);
            if (raw === null)
                return [];
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed))
                return [];
            return parsed.filter((e) => typeof e?.ev === 'string').slice(-maxEntries);
        }
        catch {
            return [];
        }
    }
    function persist() {
        if (storage === null)
            return;
        try {
            storage.setItem(STORAGE_KEY, JSON.stringify(entries));
        }
        catch {
            // quota/private mode: жертвуем старой половиной и пробуем ещё раз; дальше — молча в памяти.
            try {
                entries = entries.slice(Math.floor(entries.length / 2));
                storage.setItem(STORAGE_KEY, JSON.stringify(entries));
            }
            catch {
                /* диагностика не должна ломать приложение */
            }
        }
    }
    function online() {
        return nav?.onLine !== false; // неизвестно → считаем онлайн
    }
    function log(ev, d) {
        try {
            const entry = {
                ts: new Date(now()).toISOString(),
                sid,
                seq: ++seq,
                ev: capStr(ev, 48),
                ...(d !== undefined ? { d: sanitizeData(d) } : {}),
            };
            entries.push(entry);
            if (entries.length > maxEntries)
                entries = entries.slice(-maxEntries);
            persist();
        }
        catch {
            /* fail-safe */
        }
    }
    // ── флаш ─────────────────────────────────────────────────────────────────────────────────────
    let flushing = false;
    async function tryFlush() {
        if (flushing || entries.length === 0)
            return entries.length === 0;
        flushing = true;
        try {
            // до 5 пачек за проход — дожмёт даже полный буфер (400/120), не зациклится при пополнении
            for (let round = 0; round < 5 && entries.length > 0; round++) {
                const batch = entries.slice(0, FLUSH_BATCH);
                const headers = {
                    'content-type': 'application/json',
                    [REQUESTED_WITH_HEADER]: REQUESTED_WITH_VALUE,
                };
                const token = csrfSource();
                if (token !== undefined)
                    headers[CSRF_HEADER] = token;
                const res = await netFetch(`${baseUrl}${FLUSH_PATH}`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ entries: batch }),
                });
                if (!res.ok)
                    return false; // 401/403/5xx — буфер целиком остаётся до лучших времён
                const sent = new Set(batch.map((e) => `${e.sid}:${e.seq}`));
                entries = entries.filter((e) => !sent.has(`${e.sid}:${e.seq}`));
                persist();
            }
            return entries.length === 0;
        }
        catch {
            return false; // сетевой сбой флаша НЕ логируем (петля самовозбуждения)
        }
        finally {
            flushing = false;
        }
    }
    // ── активные пробы /healthz при упавшем WS (дискриминатор «WS vs вся сеть») ─────────────────
    let wsUp = false;
    let probeTimer = null;
    let probesLeft = 0;
    async function runProbe() {
        probesLeft -= 1;
        const started = now();
        try {
            const res = await netFetch(`${baseUrl}${HEALTHZ_PATH}`, {
                method: 'GET',
                cache: 'no-store',
            });
            log('probe', { ok: res.ok, status: res.status, dt: now() - started, online: online() });
            if (res.ok)
                void tryFlush();
        }
        catch (err) {
            log('probe', { ok: false, err: errName(err), dt: now() - started, online: online() });
        }
    }
    function probeEpisodeStop() {
        if (probeTimer !== null) {
            clearIntervalFn(probeTimer);
            probeTimer = null;
        }
    }
    function probeEpisodeStart() {
        if (probeTimer !== null)
            return;
        probesLeft = probesPerEpisode;
        void runProbe();
        probeTimer = setIntervalFn(() => {
            if (wsUp || probesLeft <= 0) {
                probeEpisodeStop();
                return;
            }
            void runProbe();
        }, probeIntervalMs);
    }
    // ── обёртки транспортных портов ──────────────────────────────────────────────────────────────
    function wrapFetch(base) {
        const inner = base ?? defaultFetch();
        return async (input, init) => {
            const path = pathOf(input);
            // свои же каналы не логируем (рекурсия/шум); probe логирует себя сам
            if (path.endsWith(FLUSH_PATH) || path.endsWith(HEALTHZ_PATH))
                return inner(input, init);
            const m = capStr(init?.method ?? input?.method ?? 'GET', 8);
            const started = now();
            try {
                const res = await inner(input, init);
                const dt = now() - started;
                if (res.status >= 500 || res.status === 429)
                    log('rest.err', { path, m, status: res.status, dt });
                else if (dt >= 8000)
                    log('rest.slow', { path, m, status: res.status, dt });
                return res;
            }
            catch (err) {
                log('rest.fail', { path, m, err: errName(err), dt: now() - started, online: online() });
                throw err;
            }
        };
    }
    let wsAttempt = 0;
    function wrapWs(base) {
        const factory = base ?? defaultWsCtor();
        return (url, protocols) => {
            const startedAt = now();
            let openedAt = null;
            const host = hostOf(url);
            log('ws.connecting', { n: ++wsAttempt, host, online: online() });
            const inner = factory(url, protocols);
            const facade = {
                send: (data) => inner.send(data),
                close: (code, reason) => inner.close(code, reason),
                get readyState() {
                    return inner.readyState;
                },
                onopen: null,
                onmessage: null,
                onclose: null,
                onerror: null,
            };
            inner.onopen = (ev) => {
                openedAt = now();
                wsUp = true;
                probeEpisodeStop();
                log('ws.open', { n: wsAttempt, host, dt: openedAt - startedAt });
                void tryFlush();
                facade.onopen?.(ev);
            };
            inner.onmessage = (ev) => facade.onmessage?.(ev);
            inner.onerror = (ev) => {
                log('ws.error', { n: wsAttempt, online: online() });
                facade.onerror?.(ev);
            };
            inner.onclose = (ev) => {
                wsUp = false;
                // code 1006 без ws.open = соединение не установилось; dtOpen — сколько прожили после open.
                log('ws.close', {
                    n: wsAttempt,
                    host,
                    code: ev.code,
                    reason: capStr(ev.reason ?? '', 80),
                    dtOpen: openedAt !== null ? now() - openedAt : null,
                    dtConn: now() - startedAt,
                    online: online(),
                });
                probeEpisodeStart();
                facade.onclose?.(ev);
            };
            return facade;
        };
    }
    // ── жизненный цикл ───────────────────────────────────────────────────────────────────────────
    let started = false;
    let flushTimer = null;
    let startTimeout = null;
    const listeners = [];
    function listen(target, type, handler) {
        if (target === null)
            return;
        try {
            target.addEventListener(type, handler);
            listeners.push({ target, type, handler });
        }
        catch {
            /* fail-safe */
        }
    }
    function start() {
        if (started)
            return;
        started = true;
        log('app.start', {
            ua: capStr(nav?.userAgent ?? '', 140),
            online: online(),
            vis: doc?.visibilityState ?? '',
            buffered: entries.length,
        });
        listen(win, 'online', () => {
            log('net.online');
            startTimeout = setTimeoutFn(() => void tryFlush(), 2000);
        });
        listen(win, 'offline', () => log('net.offline'));
        listen(doc, 'visibilitychange', () => log('vis', { s: doc?.visibilityState ?? '' }));
        listen(win, 'error', (ev) => log('js.error', { msg: capStr(ev?.message ?? 'error', 160) }));
        listen(win, 'unhandledrejection', (ev) => log('js.reject', { msg: errName(ev?.reason) }));
        flushTimer = setIntervalFn(() => {
            if (entries.length > 0 && online())
                void tryFlush();
        }, flushIntervalMs);
        startTimeout = setTimeoutFn(() => void tryFlush(), 3000);
    }
    function stop() {
        if (!started)
            return;
        started = false;
        for (const l of listeners.splice(0)) {
            try {
                l.target.removeEventListener(l.type, l.handler);
            }
            catch {
                /* fail-safe */
            }
        }
        if (flushTimer !== null)
            clearIntervalFn(flushTimer);
        flushTimer = null;
        if (startTimeout !== null)
            clearTimeoutFn(startTimeout);
        startTimeout = null;
        probeEpisodeStop();
    }
    return {
        log,
        wrapFetch,
        wrapWs,
        start,
        stop,
        flushNow: () => tryFlush(),
        bufferSize: () => entries.length,
    };
}
//# sourceMappingURL=connection-diag.js.map